import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  filtersPerFacetValue,
  facetBreadcrumb,
  facetLabelsProps,
  facetsLabels,
  defaultTab,
  tabPerFacetProps
} from '../controllers/controller';
import {
  BreadcrumbManager,
  BreadcrumbValue,
  FacetBreadcrumb,
  FacetValue,
  NumericFacetBreadcrumb,
  NumericFacetValue,
  CategoryFacetBreadcrumb
} from '@coveo/headless';
import Clear from './clear';
import SecondCall from './second-call-context';
import { createDataLayerString } from '../../helpers/helpers';
import { useTab } from './tab';
type FacetBreadcrumbsProps = {
  extraClasses?: string;
  clearLabel: string;
};

function FacetBreadcrumbs({ extraClasses, clearLabel }: FacetBreadcrumbsProps) {
  const [state, setState] = useState(
    (facetBreadcrumb as BreadcrumbManager).state
  );
  const secondCallContext = useContext(SecondCall);

  useEffect(() => {
    (facetBreadcrumb as BreadcrumbManager).subscribe(() =>
      setState((facetBreadcrumb as BreadcrumbManager).state)
    );
  }, []);

  const handleFacetClick = (breadcrumb: CategoryFacetBreadcrumb) => {
    if (breadcrumb.path.length > 1) {
      window.dispatchEvent(
        new CustomEvent('deselectFacetTree', {
          detail: {
            value: breadcrumb.path[breadcrumb.path.length - 2]
          }
        })
      );
    } else {
      breadcrumb.deselect();
    }
  };

  const renderCategoryFacetBreadcrumbs = (
    breadcrumbs: CategoryFacetBreadcrumb[]
  ) => {
    return (
      <>
        {breadcrumbs.map(breadcrumb =>
          breadcrumb.path.map((facet, index) => {
            return (
              <li key={index}>
                <button
                  className="btn btn--secondary btn--facet"
                  type="button"
                  onClick={() => handleFacetClick(breadcrumb)}
                >
                  {facet.value}
                </button>
              </li>
            );
          })
        )}
      </>
    );
  };
  const getRangeFacetLabel = (obj: { start: number; end: number }) => {
    const rangeFacets = facetsLabels.filter((el: facetLabelsProps) => el.range);
    let parentEl: facetLabelsProps | undefined = undefined,
      el: number | undefined = undefined;

    rangeFacets.find((rangeObj: facetLabelsProps) => {
      rangeObj.rangeValues.forEach((range, index) => {
        if (range.start === obj.start && range.end === obj.end) {
          parentEl = rangeObj;
          el = index;
          return;
        }
      });
    });

    if (!parentEl || el === undefined) return '';
    const parentLabels = parentEl as facetLabelsProps;
    if (parentLabels?.rangeStars) {
      return `${parentLabels.rangeStars[el]} ${parentLabels.rangeLabels[el]}`;
    } else {
      return parentLabels.rangeLabels[el];
    }
  };
  const renderSecondFacets = (
    value: string | { start: number; end: number },
    index: number
  ) => {
    return (
      <li key={index}>
        <button
          className="btn btn--secondary btn--facet"
          type="button"
          onClick={() => {
            if (secondCallContext.setSelectedFacets === undefined) return;
            secondCallContext.setSelectedFacets(facets =>
              facets.filter(facet => facet.value !== value)
            );
          }}
        >
          {!(value as { start: number }).start ? (
            <>{value as string}</>
          ) : (
            <>{getRangeFacetLabel(value as { start: number; end: number })}</>
          )}
        </button>
      </li>
    );
  };

  const handleValueClick = (
    value: BreadcrumbValue<FacetValue> & BreadcrumbValue<NumericFacetValue>
  ) => {
    if (!filtersPerFacetValue?.length) {
      value.deselect();
      return;
    }

    // Check if the value directly matches a context facet
    const directFilterMatch = filtersPerFacetValue.find(
      facet => facet.facetValue === value.value.value
    );

    if (directFilterMatch) {
      // Remove context if it exists
      const { tab } = directFilterMatch;

      if (tab) {
        defaultTab?.select();
      }
    } else {
      // Find the facet type that contains this value
      const matchingFacetType = state.facetBreadcrumbs.find(facet =>
        facet.values.some(facetValue => facetValue.value === value.value)
      );

      if (!matchingFacetType) {
        value.deselect();
        return;
      }

      // Find if any of the facet values match a context facet
      const matchingContextFacet = filtersPerFacetValue.find(facet =>
        matchingFacetType.values.some(
          facetValue => facetValue.value.value === facet.facetValue
        )
      );

      // If we have a matching facet type with exactly 2 values and a matching context facet
      if (matchingContextFacet && matchingFacetType.values.length === 2) {
        // Get the full context object for the matching facet
        const contextObject = filtersPerFacetValue.find(
          facet => facet.facetValue === matchingContextFacet.facetValue
        );

        // Add the context if it exists
        if (contextObject && contextObject.tab) {
          const tabController = useMemo(
            () => useTab(contextObject.tab as tabPerFacetProps),
            [contextObject.tab]
          );

          tabController?.select();
        }
      }
    }

    value.deselect();
  };

  const renderFacetBreadcrumbs = (
    breadcrumbs: FacetBreadcrumb[] | NumericFacetBreadcrumb[]
  ) => {
    return (
      <>
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={index}>
            {breadcrumb.values.map((val, index) => {
              const value = val as BreadcrumbValue<FacetValue> &
                BreadcrumbValue<NumericFacetValue>;

              return (
                <li key={index}>
                  <button
                    className="btn btn--secondary btn--facet"
                    type="button"
                    onClick={() => handleValueClick(value)}
                  >
                    {value.value.value ? (
                      <>{value.value.value}</>
                    ) : (
                      <>{getRangeFacetLabel(value.value)}</>
                    )}
                  </button>
                </li>
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  };

  if (secondCallContext.secondCall) {
    return (
      <>
        {secondCallContext.selectedFacets.length > 0 ? (
          <ul className={`facet-breadcrumbs ${extraClasses || ''}`}>
            <button
              data-layer={createDataLayerString([
                {
                  event: 'clear_all_filters',
                  event_category: 'refinement usage',
                  event_action: 'clear all filters'
                }
              ])}
              className="btn btn--primary btn--clear-all-filters"
              onClick={() => {
                if (secondCallContext.setSelectedFacets === undefined) return;
                secondCallContext?.setSelectedFacets(() => []);
              }}
              type="button"
            >
              {clearLabel}
            </button>
            {secondCallContext.selectedFacets.map((facet, index) =>
              renderSecondFacets(facet.value, index)
            )}
          </ul>
        ) : null}
      </>
    );
  }
  return (
    <>
      {state.hasBreadcrumbs ? (
        <ul className={`facet-breadcrumbs ${extraClasses || ''}`}>
          <li>
            <Clear label={clearLabel} />
          </li>
          {renderFacetBreadcrumbs(state.numericFacetBreadcrumbs)}
          {renderFacetBreadcrumbs(state.facetBreadcrumbs)}
          {renderCategoryFacetBreadcrumbs(state.categoryFacetBreadcrumbs)}
        </ul>
      ) : null}
    </>
  );
}

export default FacetBreadcrumbs;
